import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone, TableWrap } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day as dayText } from '../lib/format.ts';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * Quality: the checks to do on deliveries, production and spot checks, the alerts when something is
 * wrong, the control points that ask for the checks and the pass rate per item and supplier.
 */

type Operation = 'receipt' | 'production' | 'manual';
type Kind = 'pass_fail' | 'measure' | 'instructions';
type CheckState = 'todo' | 'pass' | 'fail';
type Stage = 'new' | 'confirmed' | 'action' | 'solved' | 'cancelled';
type Priority = 'low' | 'normal' | 'high';
type Cause = 'material' | 'machine' | 'method' | 'people' | 'measurement' | 'environment';
type Tab = 'checks' | 'alerts' | 'history' | 'points' | 'report';

interface CheckRow {
  id: string;
  name: string;
  point: string | null;
  title: string;
  kind: Kind;
  operation: Operation;
  item: string | null;
  item_name: string | null;
  document: string | null;
  partner: string | null;
  instructions: string | null;
  unit: string | null;
  tol_min: number | null;
  tol_max: number | null;
  state: CheckState;
  value: number | null;
  note: string | null;
  assigned: string | null;
  assigned_name: string | null;
  done_by_name: string | null;
  done_at: string | null;
  created_at: string;
  alert: string | null;
  alert_name: string | null;
}
interface CheckFull extends CheckRow {
  history: { name: string; state: CheckState; value: number | null; done_at: string; document: string | null }[];
}
interface AlertRow {
  id: string;
  name: string;
  title: string;
  item: string | null;
  item_name: string | null;
  check: string | null;
  check_name: string | null;
  source: string | null;
  stage: Stage;
  priority: Priority;
  root_cause: Cause | null;
  responsible: string | null;
  responsible_name: string | null;
  reported_by: string | null;
  reported_by_name: string | null;
  deadline: string | null;
  solved_at: string | null;
  created_at: string;
  updated_at: string;
}
interface AlertFull extends AlertRow {
  description: string | null;
  corrective: string | null;
  preventive: string | null;
  events: { kind: string; text: string | null; at: string; by_name: string | null }[];
  today: string;
  can: { work: boolean; edit: boolean; cancel: boolean };
}
interface Overview {
  today: string;
  kpi: { todo: number; todo_mine: number; done_30: number; failed_30: number; pass_rate: number | null; open: number; late: number; high: number };
  mine: AlertRow[];
  can: { check: boolean; manage: boolean };
}
interface PointRow {
  id: string;
  title: string;
  operation: Operation;
  item: string | null;
  item_name: string | null;
  kind: Kind;
  instructions: string | null;
  unit: string | null;
  tol_min: number | null;
  tol_max: number | null;
  one_in: number;
  responsible: string | null;
  responsible_name: string | null;
  active: boolean;
  done: number;
  failed: number;
  todo: number;
}
interface Choices {
  items: { id: string; name: string; unit: string }[];
  points: { id: string; title: string; item: string | null; item_name: string | null; kind: Kind }[];
  inspectors: { id: string; name: string }[];
}

const OPERATIONS: Operation[] = ['receipt', 'production', 'manual'];
const OP_ICON: Record<Operation, IconName> = { receipt: 'truck', production: 'factory', manual: 'eye' };
const STAGES: Stage[] = ['new', 'confirmed', 'action', 'solved'];
const STAGE: Record<Stage, { tone: Tone; icon: IconName }> = {
  new: { tone: 'info', icon: 'bell' },
  confirmed: { tone: 'warn', icon: 'alert' },
  action: { tone: 'primary', icon: 'wrench' },
  solved: { tone: 'ok', icon: 'checkCircle' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const PRIORITIES: Priority[] = ['low', 'normal', 'high'];
const PRIORITY_TONE: Record<Priority, Tone> = { low: 'neutral', normal: 'info', high: 'danger' };
const CAUSES: Cause[] = ['material', 'machine', 'method', 'people', 'measurement', 'environment'];
const isOpen = (s: Stage) => s === 'new' || s === 'confirmed' || s === 'action';
const num = (n: number) => new Intl.NumberFormat(locale(), { maximumFractionDigits: 3 }).format(n);
const pct = (n: number | null) => (n === null ? '—' : `${new Intl.NumberFormat(locale(), { maximumFractionDigits: 1 }).format(n)}%`);
const withUnit = (v: number, unit: string | null) => (unit ? `${num(v)} ${unit}` : num(v));
/** "0–4 °C", "at most 6 °C", "at least 240 g". */
function tolerance(c: { tol_min: number | null; tol_max: number | null; unit: string | null }) {
  const u = c.unit ? ` ${c.unit}` : '';
  if (c.tol_min !== null && c.tol_max !== null) return `${num(c.tol_min)}–${num(c.tol_max)}${u}`;
  if (c.tol_max !== null) return t('qa.at_most', { v: `${num(c.tol_max)}${u}` });
  if (c.tol_min !== null) return t('qa.at_least', { v: `${num(c.tol_min)}${u}` });
  return '—';
}
const within = (v: number, c: { tol_min: number | null; tol_max: number | null }) => (c.tol_min === null || v >= c.tol_min) && (c.tol_max === null || v <= c.tol_max);
const parseNum = (s: string) => {
  const v = Number(s.trim().replace(',', '.'));
  return s.trim() && Number.isFinite(v) ? v : null;
};
const initials = (n: string | null) =>
  n
    ? n
        .split(/\s+/)
        .slice(0, 2)
        .map((x) => x[0])
        .join('')
    : '?';

export function Quality() {
  const [params, setParams] = useSearchParams();
  const { data: o, reload } = useAction<Overview>('quality.overview');
  const [version, setVersion] = useState(0);
  const [alert, setAlert] = useState<string | null>(null);
  const [check, setCheck] = useState<string | null>(null);
  const [raise, setRaise] = useState(false);
  const [start, setStart] = useState<string | null | undefined>(undefined);
  const work = !!o?.can.check;
  const manage = !!o?.can.manage;
  const tabs: { key: Tab; icon: IconName }[] = work
    ? [
        { key: 'checks', icon: 'checkCircle' },
        { key: 'alerts', icon: 'alert' },
        { key: 'history', icon: 'list' },
        { key: 'points', icon: 'sliders' },
        { key: 'report', icon: 'chart' },
      ]
    : [];
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'checks') as Tab;
  const setTab = (k: Tab) => {
    const p = new URLSearchParams(params);
    if (k === 'checks') p.delete('tab');
    else p.set('tab', k);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  return (
    <Shell>
      <div className="page reports qa-page">
        <div className="page-head">
          <div>
            <h1>{t('qa.title')}</h1>
            <p className="muted">{work ? t('qa.subtitle_work') : t('qa.subtitle')}</p>
          </div>
          <span className="qa-head-actions">
            {work ? (
              <Button variant="secondary" icon="plus" onClick={() => setStart(null)}>
                {t('qa.start_check')}
              </Button>
            ) : null}
            <Button variant="primary" icon="alert" onClick={() => setRaise(true)}>
              {t('qa.raise')}
            </Button>
          </span>
        </div>
        {tabs.length ? (
          <div className="segmented tabs" role="tablist" aria-label={t('qa.sections')}>
            {tabs.map((x) => (
              <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => setTab(x.key)}>
                <Icon name={x.icon} size={16} />
                {t(`qa.tab_${x.key}` as MessageKey)}
                {x.key === 'checks' && o?.kpi.todo ? <span className="qa-tab-n">{o.kpi.todo}</span> : null}
              </button>
            ))}
          </div>
        ) : null}
        {!o ? (
          <Skeleton h={300} r="var(--radius-lg)" />
        ) : !work ? (
          <Mine o={o} onOpen={setAlert} onRaise={() => setRaise(true)} />
        ) : tab === 'checks' ? (
          <ChecksTab key={version} o={o} onChange={changed} onStart={() => setStart(null)} />
        ) : tab === 'alerts' ? (
          <AlertsTab key={version} o={o} onOpen={setAlert} onChange={changed} />
        ) : tab === 'history' ? (
          <HistoryTab key={version} onOpen={setCheck} />
        ) : tab === 'points' ? (
          <PointsTab key={version} manage={manage} onChange={changed} onStart={(id) => setStart(id)} />
        ) : (
          <ReportTab today={o.today} />
        )}
      </div>
      {alert ? <AlertDialog id={alert} onClose={() => setAlert(null)} onChange={changed} onCheck={(id) => (setAlert(null), setCheck(id))} /> : null}
      {check ? <CheckDialog id={check} onClose={() => setCheck(null)} onAlert={(id) => (setCheck(null), setAlert(id))} /> : null}
      {raise ? <RaiseDialog onClose={() => setRaise(false)} onDone={changed} /> : null}
      {start !== undefined ? <StartDialog point={start} onClose={() => setStart(undefined)} onDone={async () => (setTab('checks'), await changed())} /> : null}
    </Shell>
  );
}

// ---- everyone: raise an alert and follow it ---------------------------------------------

function Mine({ o, onOpen, onRaise }: { o: Overview; onOpen: (id: string) => void; onRaise: () => void }) {
  if (!o.mine.length)
    return (
      <section className="panel">
        <EmptyState
          icon="badgeCheck"
          title={t('qa.mine_empty')}
          body={t('qa.mine_empty_body')}
          action={
            <Button variant="primary" icon="alert" onClick={onRaise}>
              {t('qa.raise')}
            </Button>
          }
        />
      </section>
    );
  return (
    <section className="panel">
      <h2>{t('qa.your_alerts')}</h2>
      <ul className="mt-mine">
        {o.mine.map((a) => (
          <li key={a.id}>
            <button type="button" onClick={() => onOpen(a.id)}>
              <span className="mt-mine-main">
                <strong>{a.title}</strong>
                <span className="muted small">{[a.name, a.item_name, a.source, dateTime(a.created_at)].filter(Boolean).join(' · ')}</span>
                {a.responsible_name ? <span className="small">{t('qa.with_who', { name: a.responsible_name })}</span> : null}
              </span>
              <StatusBadge tone={STAGE[a.stage].tone} icon={STAGE[a.stage].icon} label={t(`qa.stage_${a.stage}` as MessageKey)} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PriorityPick({ value, onChange }: { value: Priority; onChange: (p: Priority) => void }) {
  return (
    <div className="segmented mt-prio" role="radiogroup" aria-label={t('qa.priority')}>
      {PRIORITIES.map((p) => (
        <button key={p} type="button" role="radio" aria-checked={value === p} className={`${value === p ? 'active' : ''} qa-prio-${p}`} onClick={() => onChange(p)}>
          {t(`qa.prio_${p}` as MessageKey)}
        </button>
      ))}
    </div>
  );
}

function ItemSelect({ items, value, onChange, label, none }: { items: Choices['items']; value: string; onChange: (v: string) => void; label: string; none: string }) {
  return (
    <select className="input" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{none}</option>
      {items.map((i) => (
        <option key={i.id} value={i.id}>
          {i.name}
        </option>
      ))}
    </select>
  );
}

function RaiseDialog({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }) {
  const { data: c } = useAction<Choices>('quality.choices');
  const { busy, run } = useSave();
  const [f, setF] = useState({ title: '', item: '', source: '', description: '', priority: 'normal' as Priority });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const error = f.title.trim().length < 3 ? t('qa.e_title') : null;
  const save = () => {
    setTried(true);
    if (error) return;
    void run(async () => {
      await action('quality.raise_alert', {
        title: f.title.trim(),
        item: f.item || null,
        source: f.source.trim() || null,
        description: f.description.trim() || null,
        priority: f.priority,
      });
      await onDone();
      onClose();
    }, t('qa.raised'));
  };
  return (
    <Dialog
      title={t('qa.raise')}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="send" busy={busy} onClick={save}>
            {t('qa.send_alert')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('qa.what_wrong')} error={tried ? error : null} required>
          <input className="input" value={f.title} maxLength={160} placeholder={t('qa.what_wrong_example')} onChange={(e) => set('title', e.target.value)} data-autofocus />
        </Field>
        <Field label={t('qa.item')} hint={t('qa.item_hint')}>
          <ItemSelect items={c?.items ?? []} value={f.item} onChange={(v) => set('item', v)} label={t('qa.item')} none={t('qa.no_item')} />
        </Field>
        <Field label={t('qa.source')} hint={t('qa.source_hint')}>
          <input className="input" value={f.source} maxLength={120} onChange={(e) => set('source', e.target.value)} />
        </Field>
        <Field label={t('qa.details')} hint={t('qa.details_hint')}>
          <textarea className="input" rows={3} value={f.description} maxLength={4000} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <Field label={t('qa.priority')}>
          <PriorityPick value={f.priority} onChange={(p) => set('priority', p)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- checks to do -----------------------------------------------------------------------

function ChecksTab({ o, onChange, onStart }: { o: Overview; onChange: () => Promise<void>; onStart: () => void }) {
  const [who, setWho] = useState<'' | 'me'>('');
  const [op, setOp] = useState<'' | Operation>('');
  const { data, reload } = useAction<{ checks: CheckRow[] }>('quality.checks', { state: 'todo', assigned: who || null, operation: op || null });
  const [failing, setFailing] = useState<{ c: CheckRow; value: number | null } | null>(null);
  const { toast } = useUi();
  const { busy, run } = useSave();
  const record = (c: CheckRow, input: { result?: 'pass' | 'fail'; value?: number | null; note?: string | null; priority?: Priority }) =>
    run(async () => {
      const r = await action<{ state: CheckState; alert: { id: string; name: string } | null }>('quality.do_check', { id: c.id, ...input });
      setFailing(null);
      await reload();
      await onChange();
      if (r.alert) toast(t('qa.failed_alert', { name: r.alert.name }), 'info');
      else toast(t('qa.passed', { name: c.title }));
    });
  const k = o.kpi;
  const rows = data?.checks ?? [];
  return (
    <>
      <section className="rep-kpis" aria-label={t('qa.glance')}>
        <Kpi label={t('qa.k_todo')} value={String(k.todo)} hint={k.todo_mine ? tn('qa.k_todo_mine', k.todo_mine) : t('qa.k_todo_none_mine')} />
        <Kpi label={t('qa.k_rate')} value={pct(k.pass_rate)} hint={tn('qa.k_rate_hint', k.done_30)} warn={k.pass_rate !== null && k.pass_rate < 90} />
        <Kpi label={t('qa.k_failed')} value={String(k.failed_30)} hint={t('qa.k_failed_hint')} warn={k.failed_30 > 0} />
        <Kpi label={t('qa.k_alerts')} value={String(k.open)} hint={k.late ? tn('qa.k_alerts_late', k.late) : k.high ? tn('qa.k_alerts_high', k.high) : t('qa.k_alerts_hint')} warn={k.late > 0} />
      </section>
      <div className="toolbar mt-tools">
        <div className="segmented" role="radiogroup" aria-label={t('qa.who')}>
          {(['', 'me'] as const).map((w) => (
            <button key={w} type="button" role="radio" aria-checked={who === w} className={who === w ? 'active' : ''} onClick={() => setWho(w)}>
              {t(w === '' ? 'qa.everyone' : 'qa.mine_filter')}
            </button>
          ))}
        </div>
        <select className="input mt-select" aria-label={t('qa.operation')} value={op} onChange={(e) => setOp(e.target.value as '' | Operation)}>
          <option value="">{t('qa.all_operations')}</option>
          {OPERATIONS.map((x) => (
            <option key={x} value={x}>
              {t(`qa.op_${x}` as MessageKey)}
            </option>
          ))}
        </select>
      </div>
      {!data ? (
        <Skeleton h={260} r="var(--radius-lg)" />
      ) : !rows.length ? (
        <section className="panel">
          <EmptyState
            icon="checkCircle"
            title={t('qa.todo_empty')}
            body={t('qa.todo_empty_body')}
            action={
              <Button icon="plus" onClick={onStart}>
                {t('qa.start_check')}
              </Button>
            }
          />
        </section>
      ) : (
        <div className="qa-todo">
          {rows.map((c) => (
            <CheckCard key={c.id} c={c} busy={busy} onPass={(value) => void record(c, { result: 'pass', value })} onFail={(value) => setFailing({ c, value })} />
          ))}
        </div>
      )}
      {failing ? (
        <FailDialog
          c={failing.c}
          value={failing.value}
          busy={busy}
          onClose={() => setFailing(null)}
          onFail={(note, priority) => void record(failing.c, { result: 'fail', value: failing.value, note, priority })}
        />
      ) : null}
    </>
  );
}

/** One check to do, answered right on the card: a value, pass or fail, or done. */
function CheckCard({ c, busy, onPass, onFail }: { c: CheckRow; busy: boolean; onPass: (value: number | null) => void; onFail: (value: number | null) => void }) {
  const [value, setValue] = useState('');
  const [more, setMore] = useState(false);
  const v = parseNum(value);
  const ok = v === null ? null : within(v, c);
  const id = `qa-v-${c.id}`;
  return (
    <article className={`panel qa-card op-${c.operation}`}>
      <header className="qa-card-head">
        <span className="qa-op" title={t(`qa.op_${c.operation}` as MessageKey)}>
          <Icon name={OP_ICON[c.operation]} size={18} />
        </span>
        <span className="qa-card-title">
          <strong>{c.title}</strong>
          <span className="muted small">{[c.item_name, c.document, c.partner].filter(Boolean).join(' · ') || t(`qa.op_${c.operation}` as MessageKey)}</span>
        </span>
        <span className="tk-avatar" title={c.assigned_name ?? t('qa.nobody')} aria-label={c.assigned_name ?? t('qa.nobody')}>
          {initials(c.assigned_name)}
        </span>
      </header>
      <p className="muted small qa-card-meta">
        {c.name} · {t('qa.since', { time: dateTime(c.created_at) })}
        <button type="button" className="link" onClick={() => setMore((m) => !m)} aria-expanded={more}>
          {more ? t('qa.hide_how') : t('qa.show_how')}
        </button>
      </p>
      {more ? <p className="qa-how">{c.instructions || t('qa.no_instructions')}</p> : null}
      {c.kind === 'measure' ? (
        <div className="qa-measure">
          <label htmlFor={id} className="small">
            {t('qa.measured')} <span className="muted">({t('qa.passes', { range: tolerance(c) })})</span>
          </label>
          <span className="qa-measure-row">
            <span className="qa-input-unit">
              <input
                id={id}
                className="input"
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && v !== null && (ok ? onPass(v) : onFail(v))}
              />
              {c.unit ? <span className="muted">{c.unit}</span> : null}
            </span>
            {ok === null ? null : ok ? (
              <span className="qa-verdict pass">
                <Icon name="checkCircle" size={16} /> {t('qa.within')}
              </span>
            ) : (
              <span className="qa-verdict fail">
                <Icon name="alert" size={16} /> {t('qa.outside')}
              </span>
            )}
            <Button variant={ok === false ? 'danger' : 'primary'} icon={ok === false ? 'alert' : 'check'} busy={busy} disabled={v === null} onClick={() => v !== null && (ok ? onPass(v) : onFail(v))}>
              {t('qa.record')}
            </Button>
          </span>
        </div>
      ) : c.kind === 'instructions' ? (
        <div className="qa-buttons">
          <Button variant="primary" icon="check" busy={busy} onClick={() => onPass(null)}>
            {t('qa.mark_done')}
          </Button>
        </div>
      ) : (
        <div className="qa-buttons">
          <Button variant="primary" icon="check" busy={busy} onClick={() => onPass(null)}>
            {t('qa.pass')}
          </Button>
          <Button variant="danger" icon="x" busy={busy} onClick={() => onFail(null)}>
            {t('qa.fail')}
          </Button>
        </div>
      )}
    </article>
  );
}

/** A failure opens an alert: what was wrong and how urgent it is. */
function FailDialog({ c, value, busy, onClose, onFail }: { c: CheckRow; value: number | null; busy: boolean; onClose: () => void; onFail: (note: string | null, priority: Priority) => void }) {
  const [note, setNote] = useState('');
  const [priority, setPriority] = useState<Priority>('normal');
  const [tried, setTried] = useState(false);
  const needed = c.kind !== 'measure';
  return (
    <Dialog
      title={t('qa.fail_title', { name: c.title })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            icon="alert"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (needed && !note.trim()) return;
              onFail(note.trim() || null, priority);
            }}
          >
            {t('qa.fail_and_alert')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p className="callout qa-fail-callout">
          <Icon name="alert" size={16} />
          <span>{value !== null ? t('qa.fail_measure', { value: withUnit(value, c.unit), range: tolerance(c) }) : t('qa.fail_info')}</span>
        </p>
        <Field label={t('qa.fail_note')} hint={t('qa.fail_note_hint')} error={tried && needed && !note.trim() ? t('qa.e_note') : null} required={needed}>
          <textarea className="input" rows={3} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('qa.priority')}>
          <PriorityPick value={priority} onChange={setPriority} />
        </Field>
      </div>
    </Dialog>
  );
}

function StartDialog({ point, onClose, onDone }: { point: string | null; onClose: () => void; onDone: () => Promise<void> }) {
  const { data: c } = useAction<Choices>('quality.choices');
  const { busy, run } = useSave();
  const [f, setF] = useState({ point: point ?? '', item: '', document: '', assigned: '' });
  const [tried, setTried] = useState(false);
  const p = c?.points.find((x) => x.id === f.point);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const save = () => {
    setTried(true);
    if (!f.point) return;
    void run(async () => {
      await action('quality.start_check', { point: f.point, item: p?.item ? null : f.item || null, document: f.document.trim() || null, assigned: f.assigned || null });
      await onDone();
      onClose();
    }, t('qa.started'));
  };
  return (
    <Dialog
      title={t('qa.start_check')}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="plus" busy={busy} onClick={save}>
            {t('qa.start')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('qa.point')} hint={t('qa.point_hint')} error={tried && !f.point ? t('qa.e_point') : null} required>
          <select className="input" value={f.point} onChange={(e) => set('point', e.target.value)} data-autofocus>
            <option value="">{t('qa.choose_point')}</option>
            {(c?.points ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.item_name ? `${x.title} · ${x.item_name}` : x.title}
              </option>
            ))}
          </select>
        </Field>
        {p && !p.item ? (
          <Field label={t('qa.item')} hint={t('qa.item_any_hint')}>
            <ItemSelect items={c?.items ?? []} value={f.item} onChange={(v) => set('item', v)} label={t('qa.item')} none={t('qa.no_item')} />
          </Field>
        ) : null}
        <Field label={t('qa.document')} hint={t('qa.document_hint')}>
          <input className="input" value={f.document} maxLength={60} onChange={(e) => set('document', e.target.value)} />
        </Field>
        <Field label={t('qa.assigned')}>
          <select className="input" value={f.assigned} onChange={(e) => set('assigned', e.target.value)}>
            <option value="">{t('qa.me')}</option>
            {(c?.inspectors ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Dialog>
  );
}

// ---- results ----------------------------------------------------------------------------

function resultBadge(c: { state: CheckState; kind: Kind; value: number | null; unit: string | null }) {
  if (c.state === 'todo') return <StatusBadge tone="info" icon="clock" label={t('qa.state_todo')} />;
  const label = c.value !== null ? withUnit(c.value, c.unit) : c.kind === 'instructions' ? t('qa.state_done') : t(`qa.state_${c.state}` as MessageKey);
  return <StatusBadge tone={c.state === 'pass' ? 'ok' : 'danger'} icon={c.state === 'pass' ? 'checkCircle' : 'alert'} label={label} />;
}

function HistoryTab({ onOpen }: { onOpen: (id: string) => void }) {
  const [state, setState] = useState<'done' | 'pass' | 'fail'>('done');
  const [op, setOp] = useState<'' | Operation>('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  const { data } = useAction<{ checks: CheckRow[] }>('quality.checks', { state, operation: op || null, search: search || null });
  const rows = data?.checks ?? [];
  return (
    <>
      <div className="toolbar mt-tools">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('qa.search')} aria-label={t('qa.search')} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="segmented" role="radiogroup" aria-label={t('qa.result')}>
          {(['done', 'pass', 'fail'] as const).map((s) => (
            <button key={s} type="button" role="radio" aria-checked={state === s} className={state === s ? 'active' : ''} onClick={() => setState(s)}>
              {t(`qa.filter_${s}` as MessageKey)}
            </button>
          ))}
        </div>
        <select className="input mt-select" aria-label={t('qa.operation')} value={op} onChange={(e) => setOp(e.target.value as '' | Operation)}>
          <option value="">{t('qa.all_operations')}</option>
          {OPERATIONS.map((x) => (
            <option key={x} value={x}>
              {t(`qa.op_${x}` as MessageKey)}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          icon="download"
          disabled={!rows.length}
          onClick={() =>
            downloadCsv(
              'quality-checks.csv',
              [t('qa.check'), t('qa.what'), t('qa.item'), t('qa.document'), t('qa.supplier'), t('qa.result'), t('qa.value'), t('qa.done_by'), t('qa.when')],
              rows.map((c) => [c.name, c.title, c.item_name ?? '', c.document ?? '', c.partner ?? '', t(`qa.state_${c.state}` as MessageKey), c.value ?? '', c.done_by_name ?? '', c.done_at ?? '']),
            )
          }
        >
          {t('qa.csv')}
        </Button>
      </div>
      <section className="panel">
        {!data ? (
          <Skeleton h={260} />
        ) : (
          <TableWrap>
            <Table
              head={[t('qa.what'), t('qa.result'), t('qa.document'), t('qa.done_by'), t('qa.when')]}
              phoneHide={[2, 3]}
              empty={t('qa.history_empty')}
              rows={rows.map((c) => ({
                key: c.id,
                onClick: () => onOpen(c.id),
                cells: [
                  <span key="w" className="mt-eq-name">
                    <strong>{c.title}</strong>
                    <span className="muted small">{[c.name, c.item_name].filter(Boolean).join(' · ')}</span>
                  </span>,
                  <span key="r" className="qa-result-cell">
                    {resultBadge(c)}
                    {c.alert_name ? <span className="muted small">{c.alert_name}</span> : null}
                  </span>,
                  [c.document, c.partner].filter(Boolean).join(' · ') || '—',
                  c.done_by_name ?? '—',
                  c.done_at ? dateTime(c.done_at) : '—',
                ],
              }))}
            />
          </TableWrap>
        )}
      </section>
    </>
  );
}

function CheckDialog({ id, onClose, onAlert }: { id: string; onClose: () => void; onAlert: (id: string) => void }) {
  const { data: c } = useAction<CheckFull>('quality.check', { id });
  return (
    <Dialog
      title={c ? `${c.name} · ${c.title}` : t('qa.check')}
      onClose={onClose}
      size="lg"
      footer={
        c?.alert ? (
          <span className="foot-actions">
            <Button variant="secondary" icon="alert" onClick={() => onAlert(c.alert!)}>
              {t('qa.open_alert', { name: c.alert_name ?? '' })}
            </Button>
          </span>
        ) : undefined
      }
    >
      {!c ? (
        <Skeleton h={260} />
      ) : (
        <div className="mt-detail">
          <div className="mt-badges">
            {resultBadge(c)}
            <StatusBadge tone="neutral" icon={OP_ICON[c.operation]} label={t(`qa.op_${c.operation}` as MessageKey)} />
          </div>
          <dl className="mt-facts">
            <div>
              <dt>{t('qa.item')}</dt>
              <dd>{c.item_name ?? '—'}</dd>
            </div>
            <div>
              <dt>{t('qa.document')}</dt>
              <dd>{[c.document, c.partner].filter(Boolean).join(' · ') || '—'}</dd>
            </div>
            {c.kind === 'measure' ? (
              <div>
                <dt>{t('qa.tolerance')}</dt>
                <dd>{tolerance(c)}</dd>
              </div>
            ) : null}
            <div>
              <dt>{t('qa.done_by')}</dt>
              <dd>{c.done_by_name ? `${c.done_by_name} · ${dateTime(c.done_at!)}` : (c.assigned_name ?? t('qa.nobody'))}</dd>
            </div>
          </dl>
          {c.note ? <p className="mt-desc">{c.note}</p> : null}
          {c.instructions ? (
            <section>
              <h3 className="mt-h3">{t('qa.how')}</h3>
              <p className="qa-how">{c.instructions}</p>
            </section>
          ) : null}
          <section>
            <h3 className="mt-h3">{t('qa.last_results')}</h3>
            {c.history.length ? (
              <ol className="qa-trend">
                {c.history.map((h) => (
                  <li key={h.name} className={h.state} title={`${h.name} · ${dateTime(h.done_at)}`}>
                    <span className="qa-trend-v">{h.value !== null ? withUnit(h.value, c.unit) : t(`qa.state_${h.state}` as MessageKey)}</span>
                    <span className="muted small">{dayText(h.done_at)}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="muted small">{t('qa.no_last_results')}</p>
            )}
          </section>
        </div>
      )}
    </Dialog>
  );
}

// ---- alerts board -----------------------------------------------------------------------

function AlertCard({ a, today, onOpen, onDragStart }: { a: AlertRow; today: string; onOpen: () => void; onDragStart?: (e: React.DragEvent) => void }) {
  const late = isOpen(a.stage) && a.deadline && a.deadline < today;
  return (
    <article className={`tk-task qa-alert prio-${a.priority}${!isOpen(a.stage) ? ' closed' : ''}`} draggable={a.stage !== 'cancelled'} onDragStart={onDragStart} data-id={a.id}>
      <button className="tk-task-main" onClick={onOpen}>
        <span className="mt-card-top small">
          <span className="muted tk-task-no">{a.name}</span>
          {a.check_name ? (
            <span className="mt-chip">
              <Icon name="checkCircle" size={12} /> {a.check_name}
            </span>
          ) : null}
          {a.priority === 'high' && isOpen(a.stage) ? <span className="mt-chip urgent">{t('qa.prio_high')}</span> : null}
          {a.stage === 'cancelled' ? <span className="mt-chip">{t('qa.stage_cancelled')}</span> : null}
        </span>
        <strong>{a.title}</strong>
        {a.item_name || a.source ? <span className="small muted mt-card-where">{[a.item_name, a.source].filter(Boolean).join(' · ')}</span> : null}
        <span className="tk-task-meta small">
          {a.root_cause ? (
            <span className="muted">
              <Icon name="search" size={12} /> {t(`qa.cause_${a.root_cause}` as MessageKey)}
            </span>
          ) : null}
          {a.deadline && isOpen(a.stage) ? (
            <span className={late ? 'tk-warn' : 'muted'}>
              <Icon name="calendar" size={12} /> {dayText(a.deadline)}
            </span>
          ) : null}
          <span className="tk-avatar" title={a.responsible_name ?? t('qa.nobody')} aria-label={a.responsible_name ?? t('qa.nobody')}>
            {initials(a.responsible_name)}
          </span>
        </span>
      </button>
    </article>
  );
}

function AlertsTab({ o, onOpen, onChange }: { o: Overview; onOpen: (id: string) => void; onChange: () => Promise<void> }) {
  const { fail } = useUi();
  const [who, setWho] = useState<'' | 'me'>('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  const { data, reload } = useAction<{ today: string; alerts: AlertRow[] }>('quality.alerts', { responsible: who || null, search: search || null });
  const [rows, setRows] = useState<AlertRow[]>([]);
  useEffect(() => setRows(data?.alerts ?? []), [data]);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<Stage | null>(null);
  const [solve, setSolve] = useState<string | null>(null);
  const moveTo = async (id: string, stage: Stage) => {
    const prev = rows;
    setRows((all) => all.map((r) => (r.id === id ? { ...r, stage } : r)));
    try {
      await action('quality.move_alert', { id, stage });
      await reload();
      await onChange();
    } catch (e) {
      setRows(prev);
      fail(e);
    }
  };
  const drop = (stage: Stage) => {
    const id = dragging;
    setDragging(null);
    setOver(null);
    const a = rows.find((x) => x.id === id);
    if (!a || a.stage === stage) return;
    // Solving asks for the cause and what was done; the other moves happen at once.
    if (stage === 'solved') setSolve(a.id);
    else void moveTo(a.id, stage);
  };
  return (
    <>
      <div className="toolbar tk-board-tools mt-tools">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('qa.search_alerts')} aria-label={t('qa.search_alerts')} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="segmented" role="radiogroup" aria-label={t('qa.responsible')}>
          {(['', 'me'] as const).map((w) => (
            <button key={w} type="button" role="radio" aria-checked={who === w} className={who === w ? 'active' : ''} onClick={() => setWho(w)}>
              {t(w === '' ? 'qa.everyone' : 'qa.mine_filter')}
            </button>
          ))}
        </div>
        <span className="muted small tk-drag-hint">{t('qa.drag_hint')}</span>
      </div>
      {!data ? (
        <Skeleton h={320} r="var(--radius-lg)" />
      ) : (
        <div className="tk-board mt-board qa-board" style={{ '--cols': STAGES.length } as React.CSSProperties}>
          {STAGES.map((s) => {
            const cards = rows.filter((r) => (s === 'solved' ? r.stage === 'solved' || r.stage === 'cancelled' : r.stage === s));
            return (
              <section
                key={s}
                className={`tk-col qa-col-${s}${over === s ? ' over' : ''}`}
                aria-label={t(`qa.stage_${s}` as MessageKey)}
                onDragOver={(e) => {
                  if (!dragging) return;
                  e.preventDefault();
                  if (over !== s) setOver(s);
                }}
                onDragLeave={(e) => {
                  if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setOver(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  drop(s);
                }}
              >
                <header className="tk-col-head">
                  <Icon name={STAGE[s].icon} size={16} />
                  <strong>{t(`qa.stage_${s}` as MessageKey)}</strong>
                  <span className="muted small">{cards.length}</span>
                </header>
                <div className="tk-col-body">
                  {cards.map((a) => (
                    <AlertCard
                      key={a.id}
                      a={a}
                      today={o.today}
                      onOpen={() => onOpen(a.id)}
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', a.id);
                        setDragging(a.id);
                      }}
                    />
                  ))}
                  {!cards.length ? <p className="muted small tk-col-empty">{t(`qa.col_empty_${s}` as MessageKey)}</p> : null}
                </div>
              </section>
            );
          })}
        </div>
      )}
      {solve ? (
        <SolveDialog
          id={solve}
          onClose={() => setSolve(null)}
          onDone={async () => {
            setSolve(null);
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </>
  );
}

/** Solving an alert: the root cause, what was done about it and what keeps it from coming back. */
function SolveDialog({ id, onClose, onDone }: { id: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { data: a } = useAction<AlertFull>('quality.alert', { id });
  const { busy, run } = useSave();
  const [f, setF] = useState<{ cause: Cause | ''; corrective: string; preventive: string } | null>(null);
  const [tried, setTried] = useState(false);
  useEffect(() => {
    if (a && !f) setF({ cause: a.root_cause ?? '', corrective: a.corrective ?? '', preventive: a.preventive ?? '' });
  }, [a, f]);
  const errors = f ? { cause: !f.cause ? t('qa.e_cause') : null, corrective: !f.corrective.trim() ? t('qa.e_corrective') : null } : { cause: null, corrective: null };
  const save = () => {
    setTried(true);
    if (!a || !f || errors.cause || errors.corrective) return;
    void run(async () => {
      await action('quality.save_alert', {
        id: a.id,
        title: a.title,
        description: a.description,
        item: a.item,
        source: a.source,
        priority: a.priority,
        responsible: a.responsible,
        deadline: a.deadline,
        root_cause: f.cause,
        corrective: f.corrective.trim(),
        preventive: f.preventive.trim() || null,
      });
      await action('quality.move_alert', { id: a.id, stage: 'solved' });
      await onDone();
    }, t('qa.solved_done'));
  };
  return (
    <Dialog
      title={a ? t('qa.solve_title', { name: a.name }) : t('qa.alert')}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="checkCircle" busy={busy} onClick={save}>
            {t('qa.move_solved')}
          </Button>
        </span>
      }
    >
      {!a || !f ? (
        <Skeleton h={240} />
      ) : (
        <div className="form-stack">
          <p className="muted">{a.title}</p>
          <Field label={t('qa.root_cause')} hint={t('qa.root_cause_hint')} error={tried ? errors.cause : null} required>
            <CausePick value={f.cause} onChange={(cause) => setF({ ...f, cause })} />
          </Field>
          <Field label={t('qa.corrective')} hint={t('qa.corrective_hint')} error={tried ? errors.corrective : null} required>
            <textarea className="input" rows={3} value={f.corrective} maxLength={4000} onChange={(e) => setF({ ...f, corrective: e.target.value })} />
          </Field>
          <Field label={t('qa.preventive')} hint={t('qa.preventive_hint')}>
            <textarea className="input" rows={2} value={f.preventive} maxLength={4000} onChange={(e) => setF({ ...f, preventive: e.target.value })} />
          </Field>
        </div>
      )}
    </Dialog>
  );
}

function CausePick({ value, onChange }: { value: Cause | ''; onChange: (c: Cause) => void }) {
  return (
    <div className="qa-causes" role="radiogroup" aria-label={t('qa.root_cause')}>
      {CAUSES.map((c) => (
        <button key={c} type="button" role="radio" aria-checked={value === c} className={value === c ? 'active' : ''} onClick={() => onChange(c)}>
          <strong>{t(`qa.cause_${c}` as MessageKey)}</strong>
          <span className="muted small">{t(`qa.cause_${c}_hint` as MessageKey)}</span>
        </button>
      ))}
    </div>
  );
}

// ---- one alert --------------------------------------------------------------------------

function AlertDialog({ id, onClose, onChange, onCheck }: { id: string; onClose: () => void; onChange: () => Promise<void>; onCheck: (id: string) => void }) {
  const { data: a, reload } = useAction<AlertFull>('quality.alert', { id });
  const [edit, setEdit] = useState(false);
  const [solve, setSolve] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [note, setNote] = useState('');
  const { busy, run } = useSave();
  const refresh = async () => {
    await reload();
    await onChange();
  };
  const move = (stage: Stage, n: string | null = null) =>
    run(
      async () => {
        await action('quality.move_alert', { id, stage, note: n });
        await refresh();
      },
      t(`qa.moved_${stage}` as MessageKey),
    );
  if (!a)
    return (
      <Dialog title={t('qa.alert')} onClose={onClose} size="lg">
        <Skeleton h={320} />
      </Dialog>
    );
  if (edit) return <AlertForm initial={a} onClose={() => setEdit(false)} onSaved={refresh} />;
  const open = isOpen(a.stage);
  const work = a.can.work;
  const late = open && a.deadline && a.deadline < a.today;
  const next: Stage | null = a.stage === 'new' ? 'confirmed' : a.stage === 'confirmed' ? 'action' : null;
  // One step back, what the board also does by dragging a card to an earlier column.
  const back: Stage | null = a.stage === 'action' ? 'confirmed' : a.stage === 'confirmed' ? 'new' : null;
  return (
    <Dialog
      title={`${a.name} · ${a.title}`}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions mt-foot">
          <span className="qa-foot-left">
            {a.can.edit && open ? (
              <Button variant="ghost" icon="edit" onClick={() => setEdit(true)}>
                {t('qa.edit')}
              </Button>
            ) : null}
            {a.can.cancel && open ? (
              <Button variant="ghost" icon="x" onClick={() => setCancel(true)}>
                {t('qa.move_cancelled')}
              </Button>
            ) : null}
          </span>
          {work ? (
            <span className="mt-foot-moves">
              {open && back ? (
                <Button variant="ghost" icon="undo" busy={busy} onClick={() => void move(back)}>
                  {t('qa.move_back', { stage: t(`qa.stage_${back}` as MessageKey) })}
                </Button>
              ) : null}
              {open && next ? (
                <Button variant="secondary" icon={STAGE[next].icon} busy={busy} onClick={() => void move(next)}>
                  {t(`qa.move_${next}` as MessageKey)}
                </Button>
              ) : null}
              {open ? (
                <Button variant="primary" icon="checkCircle" onClick={() => setSolve(true)}>
                  {t('qa.move_solved')}
                </Button>
              ) : (
                <Button variant="secondary" icon="undo" busy={busy} onClick={() => void move('action')}>
                  {t('qa.reopen')}
                </Button>
              )}
            </span>
          ) : null}
        </span>
      }
    >
      <div className="mt-detail">
        <div className="mt-badges">
          <StatusBadge tone={STAGE[a.stage].tone} icon={STAGE[a.stage].icon} label={t(`qa.stage_${a.stage}` as MessageKey)} />
          <StatusBadge tone={PRIORITY_TONE[a.priority]} icon="alert" label={t(`qa.prio_${a.priority}` as MessageKey)} />
          {a.root_cause ? <StatusBadge tone="neutral" icon="search" label={t(`qa.cause_${a.root_cause}` as MessageKey)} /> : null}
        </div>
        <dl className="mt-facts">
          <div>
            <dt>{t('qa.item')}</dt>
            <dd>{a.item_name ?? '—'}</dd>
          </div>
          <div>
            <dt>{t('qa.source')}</dt>
            <dd>{a.source ?? '—'}</dd>
          </div>
          <div>
            <dt>{t('qa.from_check')}</dt>
            <dd>
              {a.check && work ? (
                <button type="button" className="link" onClick={() => onCheck(a.check!)}>
                  {a.check_name}
                </button>
              ) : (
                (a.check_name ?? '—')
              )}
            </dd>
          </div>
          <div>
            <dt>{t('qa.responsible')}</dt>
            <dd>{a.responsible_name ?? t('qa.nobody')}</dd>
          </div>
          <div>
            <dt>{t('qa.deadline')}</dt>
            <dd className={late ? 'text-danger' : undefined}>{a.deadline ? dayText(a.deadline) : '—'}</dd>
          </div>
          <div>
            <dt>{t('qa.raised_by')}</dt>
            <dd>
              {a.reported_by_name ?? '—'} · {dateTime(a.created_at)}
            </dd>
          </div>
        </dl>
        {a.description ? <p className="mt-desc">{a.description}</p> : null}
        {a.corrective || a.preventive ? (
          <div className="qa-actions">
            {a.corrective ? (
              <div className="callout mt-resolution">
                <Icon name="wrench" size={16} />
                <div>
                  <strong>{t('qa.corrective')}</strong>
                  <p>{a.corrective}</p>
                </div>
              </div>
            ) : null}
            {a.preventive ? (
              <div className="callout mt-resolution">
                <Icon name="shieldCheck" size={16} />
                <div>
                  <strong>{t('qa.preventive')}</strong>
                  <p>{a.preventive}</p>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        <section>
          <h3 className="mt-h3">{t('qa.history')}</h3>
          <ol className="mt-events">
            {a.events.map((e, i) => (
              <li key={i}>
                <span className="mt-event-dot" aria-hidden="true" />
                <span>
                  <strong>{t(`qa.ev_${e.kind}` as MessageKey)}</strong>
                  {e.text ? <span> · {eventText(e.kind, e.text)}</span> : null}
                  <span className="muted small">
                    {' '}
                    · {e.by_name ?? '—'} · {dateTime(e.at)}
                  </span>
                </span>
              </li>
            ))}
          </ol>
          <div className="mt-note-add">
            <input className="input" value={note} maxLength={300} placeholder={t('qa.note_placeholder')} aria-label={t('qa.note_placeholder')} onChange={(e) => setNote(e.target.value)} />
            <Button
              icon="note"
              disabled={!note.trim() || busy}
              onClick={() =>
                void run(async () => {
                  await action('quality.add_note', { id, note: note.trim() });
                  setNote('');
                  await reload();
                }, t('qa.note_added'))
              }
            >
              {t('qa.add_note')}
            </Button>
          </div>
        </section>
      </div>
      {solve ? (
        <SolveDialog
          id={a.id}
          onClose={() => setSolve(false)}
          onDone={async () => {
            setSolve(false);
            await refresh();
          }}
        />
      ) : null}
      {cancel ? <CancelDialog busy={busy} onClose={() => setCancel(false)} onCancel={(n) => void move('cancelled', n).then((ok) => ok && setCancel(false))} /> : null}
    </Dialog>
  );
}

/** Stage names in the history read as words: "confirmed: talked to the supplier". */
function eventText(kind: string, text: string) {
  if (kind !== 'stage') return text;
  const [stage, ...rest] = text.split(': ');
  const name = STAGES.includes(stage as Stage) || stage === 'cancelled' ? t(`qa.stage_${stage}` as MessageKey) : stage;
  return [name, rest.join(': ')].filter(Boolean).join(': ');
}

function CancelDialog({ busy, onClose, onCancel }: { busy: boolean; onClose: () => void; onCancel: (note: string) => void }) {
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  return (
    <Dialog
      title={t('qa.cancel_title')}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            icon="x"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (note.trim()) onCancel(note.trim());
            }}
          >
            {t('qa.move_cancelled')}
          </Button>
        </span>
      }
    >
      <Field label={t('qa.cancel_why')} error={tried && !note.trim() ? t('qa.e_note') : null} required>
        <textarea className="input" rows={3} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}

function AlertForm({ initial, onClose, onSaved }: { initial: AlertFull; onClose: () => void; onSaved: () => Promise<void> }) {
  const { data: c } = useAction<Choices>('quality.choices');
  const { busy, run } = useSave();
  const work = initial.can.work;
  const [f, setF] = useState({
    title: initial.title,
    description: initial.description ?? '',
    item: initial.item ?? '',
    source: initial.source ?? '',
    priority: initial.priority,
    root_cause: (initial.root_cause ?? '') as Cause | '',
    corrective: initial.corrective ?? '',
    preventive: initial.preventive ?? '',
    responsible: initial.responsible ?? '',
    deadline: initial.deadline ?? '',
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const error = f.title.trim().length < 3 ? t('qa.e_title') : null;
  const save = () => {
    setTried(true);
    if (error) return;
    void run(async () => {
      await action('quality.save_alert', {
        id: initial.id,
        title: f.title.trim(),
        description: f.description.trim() || null,
        item: f.item || null,
        source: f.source.trim() || null,
        priority: f.priority,
        root_cause: f.root_cause || null,
        corrective: f.corrective.trim() || null,
        preventive: f.preventive.trim() || null,
        responsible: f.responsible || null,
        deadline: f.deadline || null,
      });
      await onSaved();
      onClose();
    }, t('qa.saved'));
  };
  return (
    <Dialog
      title={t('qa.edit_alert', { name: initial.name })}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('qa.what_wrong')} error={tried ? error : null} required>
          <input className="input" value={f.title} maxLength={160} onChange={(e) => set('title', e.target.value)} data-autofocus />
        </Field>
        <div className="hr-two">
          <Field label={t('qa.item')}>
            <ItemSelect items={c?.items ?? []} value={f.item} onChange={(v) => set('item', v)} label={t('qa.item')} none={t('qa.no_item')} />
          </Field>
          <Field label={t('qa.source')}>
            <input className="input" value={f.source} maxLength={120} onChange={(e) => set('source', e.target.value)} />
          </Field>
        </div>
        <Field label={t('qa.details')}>
          <textarea className="input" rows={3} value={f.description} maxLength={4000} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <Field label={t('qa.priority')}>
          <PriorityPick value={f.priority} onChange={(p) => set('priority', p)} />
        </Field>
        {work ? (
          <>
            <div className="hr-two">
              <Field label={t('qa.responsible')}>
                <select className="input" value={f.responsible} onChange={(e) => set('responsible', e.target.value)}>
                  <option value="">{t('qa.nobody')}</option>
                  {(c?.inspectors ?? []).map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('qa.deadline')}>
                <input className="input" type="date" value={f.deadline} onChange={(e) => set('deadline', e.target.value)} />
              </Field>
            </div>
            <Field label={t('qa.root_cause')} hint={t('qa.root_cause_hint')}>
              <CausePick value={f.root_cause} onChange={(v) => set('root_cause', v)} />
            </Field>
            <Field label={t('qa.corrective')} hint={t('qa.corrective_hint')}>
              <textarea className="input" rows={3} value={f.corrective} maxLength={4000} onChange={(e) => set('corrective', e.target.value)} />
            </Field>
            <Field label={t('qa.preventive')} hint={t('qa.preventive_hint')}>
              <textarea className="input" rows={2} value={f.preventive} maxLength={4000} onChange={(e) => set('preventive', e.target.value)} />
            </Field>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- control points ---------------------------------------------------------------------

function PointsTab({ manage, onChange, onStart }: { manage: boolean; onChange: () => Promise<void>; onStart: (id: string) => void }) {
  const { data, reload } = useAction<{ points: PointRow[] }>('quality.points');
  const [edit, setEdit] = useState<PointRow | 'new' | null>(null);
  const rows = data?.points ?? [];
  return (
    <>
      <div className="toolbar mt-tools">
        <p className="muted small qa-points-hint">{t('qa.points_hint')}</p>
        {manage ? (
          <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
            {t('qa.add_point')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={260} r="var(--radius-lg)" />
      ) : !rows.length ? (
        <section className="panel">
          <EmptyState
            icon="sliders"
            title={t('qa.points_empty')}
            body={t('qa.points_empty_body')}
            action={
              manage ? (
                <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
                  {t('qa.add_point')}
                </Button>
              ) : undefined
            }
          />
        </section>
      ) : (
        OPERATIONS.filter((op) => rows.some((p) => p.operation === op)).map((op) => (
          <section key={op} className="panel qa-points">
            <h2>
              <Icon name={OP_ICON[op]} size={18} /> {t(`qa.op_${op}_long` as MessageKey)}
            </h2>
            <TableWrap>
              <Table
                head={[t('qa.what'), t('qa.item'), t('qa.check_kind'), t('qa.how_often'), t('qa.results'), '']}
                phoneHide={[1, 3, 4]}
                rows={rows
                  .filter((p) => p.operation === op)
                  .map((p) => ({
                    key: p.id,
                    onClick: manage ? () => setEdit(p) : undefined,
                    cells: [
                      <span key="t" className={`mt-eq-name${p.active ? '' : ' qa-off'}`}>
                        <strong>{p.title}</strong>
                        <span className="muted small">{p.active ? (p.responsible_name ?? t('qa.anyone')) : t('qa.point_off')}</span>
                      </span>,
                      p.item_name ?? (p.operation === 'manual' ? '—' : t('qa.every_item')),
                      p.kind === 'measure' ? tolerance(p) : t(`qa.kind_${p.kind}` as MessageKey),
                      p.operation === 'manual' ? t('qa.by_hand') : p.one_in > 1 ? t('qa.one_in', { n: p.one_in }) : t('qa.every_time'),
                      p.done ? (
                        <span key="r" className={p.failed ? 'text-danger' : 'text-ok'}>
                          {tn('qa.failed_of', p.failed, { done: p.done })}
                        </span>
                      ) : (
                        '—'
                      ),
                      p.active ? <IconButton key="s" icon="plus" label={t('qa.start_this')} onClick={(e) => (e.stopPropagation(), onStart(p.id))} /> : null,
                    ],
                  }))}
              />
            </TableWrap>
          </section>
        ))
      )}
      {edit ? (
        <PointForm
          initial={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={async () => {
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </>
  );
}

function PointForm({ initial, onClose, onSaved }: { initial: PointRow | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { data: c } = useAction<Choices>('quality.choices');
  const { busy, run } = useSave();
  const [f, setF] = useState({
    title: initial?.title ?? '',
    operation: initial?.operation ?? ('receipt' as Operation),
    item: initial?.item ?? '',
    kind: initial?.kind ?? ('pass_fail' as Kind),
    instructions: initial?.instructions ?? '',
    unit: initial?.unit ?? '',
    tol_min: initial?.tol_min === null || initial?.tol_min === undefined ? '' : String(initial.tol_min),
    tol_max: initial?.tol_max === null || initial?.tol_max === undefined ? '' : String(initial.tol_max),
    one_in: initial?.one_in ?? 1,
    responsible: initial?.responsible ?? '',
    active: initial?.active ?? true,
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const min = parseNum(f.tol_min);
  const max = parseNum(f.tol_max);
  const errors = {
    title: f.title.trim().length < 3 ? t('qa.e_point_title') : null,
    tol: f.kind === 'measure' && min === null && max === null ? t('qa.e_tol') : min !== null && max !== null && min > max ? t('qa.e_tol_order') : null,
    instructions: f.kind === 'instructions' && !f.instructions.trim() ? t('qa.e_instructions') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('quality.save_point', {
        id: initial?.id ?? null,
        title: f.title.trim(),
        operation: f.operation,
        item: f.item || null,
        kind: f.kind,
        instructions: f.instructions.trim() || null,
        unit: f.unit.trim() || null,
        tol_min: min,
        tol_max: max,
        one_in: f.one_in,
        responsible: f.responsible || null,
        active: f.active,
      });
      await onSaved();
      onClose();
    }, t('qa.point_saved'));
  };
  const KINDS: Kind[] = ['pass_fail', 'measure', 'instructions'];
  return (
    <Dialog
      title={initial ? t('qa.edit_point') : t('qa.add_point')}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('qa.point_title')} error={tried ? errors.title : null} required>
          <input className="input" value={f.title} maxLength={120} placeholder={t('qa.point_title_example')} onChange={(e) => set('title', e.target.value)} data-autofocus />
        </Field>
        <Field label={t('qa.when_check')}>
          <div className="qa-causes qa-ops" role="radiogroup" aria-label={t('qa.when_check')}>
            {OPERATIONS.map((op) => (
              <button key={op} type="button" role="radio" aria-checked={f.operation === op} className={f.operation === op ? 'active' : ''} onClick={() => set('operation', op)}>
                <strong>
                  <Icon name={OP_ICON[op]} size={16} /> {t(`qa.op_${op}` as MessageKey)}
                </strong>
                <span className="muted small">{t(`qa.op_${op}_hint` as MessageKey)}</span>
              </button>
            ))}
          </div>
        </Field>
        <div className="hr-two">
          <Field label={t('qa.item')} hint={t('qa.point_item_hint')}>
            <ItemSelect items={c?.items ?? []} value={f.item} onChange={(v) => set('item', v)} label={t('qa.item')} none={f.operation === 'manual' ? t('qa.no_item') : t('qa.every_item')} />
          </Field>
          {f.operation !== 'manual' ? (
            <Field label={t('qa.how_often')} hint={t('qa.how_often_hint')}>
              <select className="input" value={f.one_in} onChange={(e) => set('one_in', Number(e.target.value))}>
                {[1, 2, 3, 4, 5, 10, 20].map((n) => (
                  <option key={n} value={n}>
                    {n === 1 ? t('qa.every_time') : t('qa.one_in', { n })}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
        </div>
        <Field label={t('qa.check_kind')}>
          <div className="segmented" role="radiogroup" aria-label={t('qa.check_kind')}>
            {KINDS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={f.kind === k} className={f.kind === k ? 'active' : ''} onClick={() => set('kind', k)}>
                {t(`qa.kind_${k}` as MessageKey)}
              </button>
            ))}
          </div>
        </Field>
        {f.kind === 'measure' ? (
          <div className="qa-tol">
            <Field label={t('qa.tol_min')} error={tried ? errors.tol : null}>
              <input className="input" inputMode="decimal" value={f.tol_min} onChange={(e) => set('tol_min', e.target.value)} />
            </Field>
            <Field label={t('qa.tol_max')}>
              <input className="input" inputMode="decimal" value={f.tol_max} onChange={(e) => set('tol_max', e.target.value)} />
            </Field>
            <Field label={t('qa.unit')} hint={t('qa.unit_hint')}>
              <input className="input" value={f.unit} maxLength={20} onChange={(e) => set('unit', e.target.value)} />
            </Field>
          </div>
        ) : null}
        <Field label={t('qa.instructions')} hint={t('qa.instructions_hint')} error={tried ? errors.instructions : null} required={f.kind === 'instructions'}>
          <textarea className="input" rows={3} value={f.instructions} maxLength={4000} onChange={(e) => set('instructions', e.target.value)} />
        </Field>
        <Field label={t('qa.who_checks')} hint={t('qa.who_checks_hint')}>
          <select className="input" value={f.responsible} onChange={(e) => set('responsible', e.target.value)}>
            <option value="">{t('qa.anyone')}</option>
            {(c?.inspectors ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="setting-row">
          <div>
            <strong>{t('qa.point_active')}</strong>
            <p className="muted">{t('qa.point_active_hint')}</p>
          </div>
          <Switch checked={f.active} onChange={(x) => set('active', x)} label={t('qa.point_active')} />
        </div>
      </div>
    </Dialog>
  );
}

// ---- report -----------------------------------------------------------------------------

interface RateRow {
  id?: string;
  name: string;
  done: number;
  failed: number;
  rate: number | null;
}

function ReportTab({ today }: { today: string }) {
  const [month, setMonth] = useState(today.slice(0, 7));
  const from = `${month}-01`;
  const last = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).toISOString().slice(0, 10);
  const to = last > today ? today : last;
  const { data } = useAction<{
    total: { done: number; failed: number; rate: number | null };
    points: RateRow[];
    items: RateRow[];
    suppliers: RateRow[];
    causes: { cause: Cause | 'unknown'; n: number }[];
    alerts: { raised: number; solved: number; days_to_solve: number | null };
  }>('quality.report', { from, to: to < from ? from : to });
  const rateTable = (rows: RateRow[], head: string) => (
    <TableWrap>
      <Table
        head={[head, t('qa.checks_n'), t('qa.failed_n'), t('qa.pass_rate')]}
        empty={t('qa.no_results_month')}
        rows={rows.map((r, i) => ({
          key: r.id ?? `${r.name}-${i}`,
          cells: [r.name, String(r.done), r.failed ? <span className="text-danger">{r.failed}</span> : '0', <RateBar key="b" rate={r.rate} />],
        }))}
      />
    </TableWrap>
  );
  const maxCause = Math.max(1, ...(data?.causes ?? []).map((c) => c.n));
  return (
    <div className="lu-report">
      <div className="lu-day-tools">
        <input className="input lu-date" type="month" value={month} max={today.slice(0, 7)} onChange={(e) => e.target.value && setMonth(e.target.value)} aria-label={t('qa.month')} />
        <Button
          size="sm"
          icon="download"
          disabled={!data?.items.length}
          onClick={() =>
            data &&
            downloadCsv(
              `quality-${month}.csv`,
              [t('qa.item'), t('qa.checks_n'), t('qa.failed_n'), t('qa.pass_rate')],
              data.items.map((r) => [r.name, r.done, r.failed, r.rate ?? '']),
            )
          }
        >
          {t('qa.csv')}
        </Button>
      </div>
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : (
        <>
          <section className="rep-kpis" aria-label={t('qa.month')}>
            <Kpi label={t('qa.k_r_checks')} value={String(data.total.done)} hint={tn('qa.k_r_failed', data.total.failed)} />
            <Kpi label={t('qa.k_rate')} value={pct(data.total.rate)} hint={t('qa.k_r_rate_hint')} warn={data.total.rate !== null && data.total.rate < 90} />
            <Kpi label={t('qa.k_r_alerts')} value={String(data.alerts.raised)} hint={tn('qa.k_r_solved', data.alerts.solved)} />
            <Kpi
              label={t('qa.k_r_days')}
              value={
                data.alerts.days_to_solve === null
                  ? '—'
                  : data.alerts.days_to_solve < 1
                    ? tn('qa.hours', Math.max(1, Math.round(data.alerts.days_to_solve * 24)))
                    : tn('qa.days', Math.round(data.alerts.days_to_solve * 10) / 10)
              }
              hint={t('qa.k_r_days_hint')}
            />
          </section>
          <div className="lu-report-grid mt-report-grid">
            <section className="panel">
              <h2>{t('qa.by_item')}</h2>
              {rateTable(data.items, t('qa.item'))}
            </section>
            <section className="panel">
              <h2>{t('qa.by_supplier')}</h2>
              {rateTable(data.suppliers, t('qa.supplier'))}
            </section>
            <section className="panel">
              <h2>{t('qa.by_point')}</h2>
              {rateTable(data.points, t('qa.point'))}
            </section>
            <section className="panel">
              <h2>{t('qa.by_cause')}</h2>
              {data.causes.length ? (
                <ul className="qa-cause-bars">
                  {data.causes.map((c) => (
                    <li key={c.cause}>
                      <span>{t(`qa.cause_${c.cause}` as MessageKey)}</span>
                      <span className="qa-bar" aria-hidden="true">
                        <span style={{ width: `${(c.n / maxCause) * 100}%` }} />
                      </span>
                      <strong>{c.n}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted small">{t('qa.no_alerts_month')}</p>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function RateBar({ rate }: { rate: number | null }) {
  if (rate === null) return <span>—</span>;
  return (
    <span className={`qa-rate${rate < 90 ? ' low' : ''}`}>
      <span className="qa-bar" aria-hidden="true">
        <span style={{ width: `${rate}%` }} />
      </span>
      {pct(rate)}
    </span>
  );
}
